// Checklist de prontidao e atividade (ultimo erro, ultimos envios, execucoes).

import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Circle, History } from "lucide-react";
import { findCategory, type ReadinessItem } from "@lucralink/shared";
import { Card, CardContent } from "@/components/ui/card";
import { formatPriceCents } from "@/lib/marketplace";
import { relativeTime } from "@/lib/auto-promotions";
import type { AutoPromotionRun, AutoPromotionSentItem } from "@/lib/hooks/useAutoPromotions";
import { cn } from "@/lib/utils";

export function ReadinessCard({ items }: { items: readonly ReadinessItem[] }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="mb-3 text-sm font-semibold text-foreground">Antes de ligar</h2>
        <ul className="space-y-2.5">
          {items.map((item) => (
            <li key={item.key} className="flex items-start gap-2.5 text-sm">
              {item.ok ? (
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
              ) : (
                <Circle size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
              <div className="min-w-0">
                <p className={cn("font-medium", item.ok ? "text-foreground" : "text-muted-foreground")}>
                  {item.label}
                  <span className="sr-only">{item.ok ? " (ok)" : " (pendente)"}</span>
                </p>
                {item.hint && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {item.hint}{" "}
                    {item.key === "credential" && (
                      <Link to="/app/account" className="text-primary underline-offset-2 hover:underline">
                        Abrir Credenciais
                      </Link>
                    )}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

const STATUS_LABEL: Readonly<Record<AutoPromotionRun["status"], string>> = {
  sent: "Enviado",
  partial: "Parcial",
  empty: "Sem ofertas novas",
  skipped: "Aguardando",
  error: "Erro",
};

const STATUS_TONE: Readonly<Record<AutoPromotionRun["status"], string>> = {
  sent: "text-primary",
  partial: "text-[hsl(var(--gold))]",
  empty: "text-muted-foreground",
  skipped: "text-muted-foreground",
  error: "text-destructive",
};

interface ActivityCardProps {
  readonly runs: readonly AutoPromotionRun[];
  readonly sent: readonly AutoPromotionSentItem[];
  readonly lastError: string | null;
  readonly lastErrorAt: string | null;
  readonly nextRunAt: string | null;
  readonly enabled: boolean;
}

function SentList({ sent }: { sent: readonly AutoPromotionSentItem[] }): JSX.Element {
  if (sent.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
        Nada enviado ainda.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {sent.map((item) => (
        <li key={item.item_id} className="flex items-center gap-3 py-2.5">
          {item.image_url ? (
            <img src={item.image_url} alt="" width={40} height={40} loading="lazy" className="h-10 w-10 shrink-0 rounded-md object-cover" />
          ) : (
            <div className="h-10 w-10 shrink-0 rounded-md bg-[hsl(var(--surface-raised))]" aria-hidden="true" />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-foreground">{item.title ?? item.item_id}</p>
            <p className="text-xs text-muted-foreground">
              {formatPriceCents(item.price_cents) ?? "Preço não informado"} · {relativeTime(item.sent_at)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ActivityCard({ runs, sent, lastError, lastErrorAt, nextRunAt, enabled }: ActivityCardProps): JSX.Element {
  return (
    <Card>
      <CardContent className="space-y-5 p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <History size={16} className="text-primary" aria-hidden="true" /> Atividade
          </h2>
          {enabled && nextRunAt && (
            <span className="text-xs text-muted-foreground">Próximo envio {relativeTime(nextRunAt)}</span>
          )}
        </div>
        {lastError && (
          <div role="alert" className="flex gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              Último erro{lastErrorAt ? ` (${relativeTime(lastErrorAt)})` : ""}: {lastError}
            </span>
          </div>
        )}
        <section aria-label="Últimos envios">
          <h3 className="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Últimos envios</h3>
          <SentList sent={sent} />
        </section>
        {runs.length > 0 && (
          <section aria-label="Execuções recentes">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Execuções recentes</h3>
            <ul className="space-y-1.5 text-xs">
              {runs.map((run) => (
                <li key={run.id} className="flex flex-wrap gap-x-2">
                  <span className={cn("font-medium", STATUS_TONE[run.status])}>{STATUS_LABEL[run.status]}</span>
                  <span className="text-muted-foreground">
                    {relativeTime(run.started_at)}
                    {run.category_id ? ` · ${findCategory(run.category_id)?.name ?? run.category_id}` : ""}
                    {run.sent_count > 0 ? ` · ${run.sent_count} ${run.sent_count === 1 ? "enviado" : "enviados"}` : ""}
                  </span>
                  {run.message && <span className="w-full text-muted-foreground/80">{run.message}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
